import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import {
  downloadTreasuryPeriodReportPdf,
  getTreasuryPeriod,
  getTreasuryPeriodReport,
  listTreasuryPeriods,
} from "../../lienzo_de_trabajo/api/cierresTesoreriaApi";
import CajaCierreTable from "../../lienzo_de_trabajo/components/CajaCierreTable";
import CierresSection from "../../lienzo_de_trabajo/components/CierresSection";
import LienzoFilters from "../../lienzo_de_trabajo/components/LienzoFilters";
import MovimientosCierreTable from "../../lienzo_de_trabajo/components/MovimientosCierreTable";
import CierreReportHeader from "../components/CierreReportHeader";
import CierreResumen from "../components/CierreResumen";

const VALID_TABS = ["summary", "movements", "cash"];

function Pill({ children, sx }) {
  return (
    <Box
      sx={{
        px: 2.5,
        py: 1.1,
        bgcolor: "secondary.50",
        borderRadius: "33px",
        boxShadow: "inset 0px 0px 3px rgba(142, 142, 159, 0.5)",
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

function errorToMessage(error) {
  return (
    error?.response?.data?.detail ||
    error?.response?.data?.message ||
    error?.data?.detail ||
    error?.data?.message ||
    error?.message ||
    "No se pudo completar la operación."
  );
}

function sortClosuresDesc(closures) {
  return [...closures].sort((a, b) => {
    const aDate = a.periodEnd || a.generatedAt || "";
    const bDate = b.periodEnd || b.generatedAt || "";
    return String(bDate).localeCompare(String(aDate));
  });
}

function HistorialTabs({ value, onChange }) {
  return (
    <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
      <Tabs
        value={value}
        onChange={(_, nextValue) => onChange(nextValue)}
        sx={{
          minHeight: 42,
          "& .MuiTab-root": {
            minHeight: 42,
            textTransform: "none",
            fontSize: 13,
            fontWeight: 800,
          },
        }}
      >
        <Tab label="Resumen" value="summary" />
        <Tab label="Movimientos del Cierre" value="movements" />
        <Tab label="Caja del Cierre" value="cash" />
      </Tabs>
    </Box>
  );
}

export default function TesoreriaHistorialCierresPage() {
  const navigate = useNavigate();
  const { periodId } = useParams();
  const [searchParams] = useSearchParams();
  const requestedTab = searchParams.get("tab");
  const activeTab = VALID_TABS.includes(requestedTab) ? requestedTab : "summary";

  const [filters, setFilters] = useState({ search: "", dateFrom: "", dateTo: "" });
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [closures, setClosures] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [currentClosure, setCurrentClosure] = useState(null);
  const [loadingClosure, setLoadingClosure] = useState(false);
  const [closureError, setClosureError] = useState("");
  const [report, setReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [reportError, setReportError] = useState("");
  const [downloadingPdfId, setDownloadingPdfId] = useState(null);
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });

  const dateError = useMemo(() => {
    if (filters.dateFrom && filters.dateTo && filters.dateFrom > filters.dateTo) {
      return "La fecha desde no puede ser posterior a la fecha hasta.";
    }
    return "";
  }, [filters.dateFrom, filters.dateTo]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearch(filters.search || "");
    }, 350);

    return () => window.clearTimeout(timeoutId);
  }, [filters.search]);

  const queryFilters = useMemo(
    () =>
      dateError
        ? { search: debouncedSearch, dateFrom: "", dateTo: "" }
        : {
            search: debouncedSearch,
            dateFrom: filters.dateFrom,
            dateTo: filters.dateTo,
          },
    [dateError, debouncedSearch, filters.dateFrom, filters.dateTo]
  );

  const fetchClosures = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const completed = await listTreasuryPeriods({
        ...queryFilters,
        status: "closed",
      });
      setClosures(sortClosuresDesc(completed));
    } catch (err) {
      setClosures([]);
      setError(errorToMessage(err));
    } finally {
      setLoading(false);
    }
  }, [queryFilters]);

  useEffect(() => {
    if (periodId) return;
    fetchClosures();
  }, [fetchClosures, periodId]);

  useEffect(() => {
    if (!periodId) {
      setCurrentClosure(null);
      setClosureError("");
      setLoadingClosure(false);
      return;
    }

    let alive = true;

    (async () => {
      setLoadingClosure(true);
      setClosureError("");

      try {
        const closure = await getTreasuryPeriod(periodId);
        if (!alive) return;
        if (closure.status !== "COMPLETED") {
          setCurrentClosure(null);
          setClosureError("El período seleccionado no pertenece al historial de cierres completados.");
          return;
        }
        setCurrentClosure(closure);
      } catch (err) {
        if (!alive) return;
        setCurrentClosure(null);
        setClosureError(errorToMessage(err));
      } finally {
        if (alive) setLoadingClosure(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [periodId]);

  useEffect(() => {
    if (!periodId) {
      setReport(null);
      setReportError("");
      setLoadingReport(false);
      return;
    }

    let alive = true;
    setLoadingReport(true);
    setReportError("");
    getTreasuryPeriodReport(periodId)
      .then((nextReport) => {
        if (alive) setReport(nextReport);
      })
      .catch((err) => {
        if (!alive) return;
        setReport(null);
        setReportError(errorToMessage(err));
      })
      .finally(() => {
        if (alive) setLoadingReport(false);
      });

    return () => {
      alive = false;
    };
  }, [periodId]);

  useEffect(() => {
    if (!periodId) return;
    if (requestedTab && VALID_TABS.includes(requestedTab)) return;
    navigate(`/tesoreria/historial/${periodId}?tab=summary`, { replace: true });
  }, [navigate, periodId, requestedTab]);

  const showToast = (severity, message) => {
    setToast({ open: true, severity, message });
  };

  const handleBack = () => {
    navigate(periodId ? "/tesoreria/historial" : "/tesoreria");
  };

  const handleTabChange = (nextTab) => {
    if (!periodId) return;
    navigate(`/tesoreria/historial/${periodId}?tab=${nextTab}`);
  };

  const handleViewReport = (closure) => {
    navigate(`/tesoreria/historial/${closure.id}?tab=summary`);
  };

  const handleSelectCategory = (movementType, category) => {
    const categoryId = category.categoryId ?? "__uncategorized__";
    const params = new URLSearchParams({
      tab: "movements",
      movementType,
      categoryId: String(categoryId),
    });
    navigate(`/tesoreria/historial/${periodId}?${params.toString()}`);
  };

  const handleDownloadPdf = async (closure) => {
    if (!closure?.id || downloadingPdfId) return;

    setDownloadingPdfId(closure.id);
    try {
      await downloadTreasuryPeriodReportPdf(closure);
      showToast("success", "Reporte PDF descargado correctamente.");
    } catch (err) {
      showToast("error", errorToMessage(err) || "No se pudo descargar el reporte PDF.");
    } finally {
      setDownloadingPdfId(null);
    }
  };

  const detailNavigation = (
    <HistorialTabs value={activeTab} onChange={handleTabChange} />
  );
  const showDetail = Boolean(periodId && currentClosure && !loadingClosure && !closureError);
  const movementInitialFilters = useMemo(
    () => {
      const movementType = searchParams.get("movementType") || "";
      const categoryId = searchParams.get("categoryId") || "";
      return {
        movementType,
        categoryId,
        status: movementType && categoryId ? "confirmed" : "",
      };
    },
    [searchParams]
  );

  return (
    <Box sx={{ width: "100%", p: 2 }}>
      <Stack spacing={2}>
        <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between" }}>
          <IconButton
            onClick={handleBack}
            sx={{
              width: 36,
              height: 36,
              bgcolor: "secondary.500",
              "&:hover": { bgcolor: "secondary.600" },
            }}
          >
            <ArrowBackIcon sx={{ color: "logoblanco", fontSize: 32 }} />
          </IconButton>

          <Stack sx={{ flex: 1, alignItems: "center", pr: "48px" }}>
            <Pill sx={{ bgcolor: "secondary.200" }}>
              <Typography sx={{ fontSize: 18, fontWeight: 600, color: "primary.900" }}>
                Historial de Cierres
              </Typography>
            </Pill>
          </Stack>
        </Stack>

        <Paper
          elevation={0}
          sx={{
            p: periodId ? 0 : { xs: 1.5, sm: 2 },
            borderRadius: periodId ? 0 : "20px",
            bgcolor: periodId ? "transparent" : "rgba(255,255,255,0.86)",
            boxShadow: periodId ? "none" : "0 4px 12px rgba(0,0,0,0.05)",
            minHeight: { xs: 360, md: "calc(100vh - 188px)" },
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
          }}
        >
          <Stack spacing={2} sx={{ flex: 1, minHeight: 0 }}>
            {!periodId ? (
              <>
                <LienzoFilters filters={filters} onChange={setFilters} dateError={dateError} />
                <CierresSection
                  title="Cierres completados"
                  emptyMessage="No hay cierres completados."
                  closures={closures}
                  variant="completed"
                  loading={loading}
                  error={error || dateError}
                  downloadingPdfId={downloadingPdfId}
                  showPdfAction
                  onViewReport={handleViewReport}
                  onDownloadPdf={handleDownloadPdf}
                />
              </>
            ) : null}

            {loadingClosure ? (
              <Stack alignItems="center" spacing={1.2} sx={{ py: 4 }}>
                <CircularProgress size={30} />
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: "primary.900" }}>
                  Cargando cierre seleccionado...
                </Typography>
              </Stack>
            ) : null}

            {!loadingClosure && closureError ? (
              <Alert severity="error" sx={{ borderRadius: "14px" }}>
                {closureError}
              </Alert>
            ) : null}

            {showDetail ? (
              <CierreReportHeader period={currentClosure} summary={report?.summary} />
            ) : null}

            {activeTab === "summary" && showDetail ? (
              <CierreResumen
                report={report}
                loading={loadingReport}
                error={reportError}
                navigation={detailNavigation}
                onSelectCategory={handleSelectCategory}
              />
            ) : null}

            {activeTab === "movements" && showDetail ? (
              <Box sx={{ flex: 1, minHeight: 0 }}>
                <MovimientosCierreTable
                  period={currentClosure}
                  readonly
                  navigation={detailNavigation}
                  hideHeader
                  initialFilters={movementInitialFilters}
                />
              </Box>
            ) : null}

            {activeTab === "cash" && showDetail ? (
              <CajaCierreTable
                period={currentClosure}
                readonly
                navigation={detailNavigation}
                hideHeader
              />
            ) : null}
          </Stack>
        </Paper>
      </Stack>

      <Snackbar
        open={toast.open}
        autoHideDuration={3500}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
          severity={toast.severity}
          sx={{ width: "100%", borderRadius: "14px" }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
